// @givmo/app-admin — Steps Competition PUBLISH confirm (EP-WEB10).
//
// Publish is the money moment: it places a wallet hold for the prize (`POST …/{id}/publish`, SC-BE3).
// So it is confirm-gated and the confirm states the full cost BEFORE the click, with the wallet's
// current available balance next to it.
//
// COST: exactly `prize_amount`, with NO platform fee added — company donations resolve fees with
// `cover_fees=False`, so the wallet is debited the prize and nothing more (`compute_full_payout_cost`).
// Do not present a "total" that differs from the prize; there isn't one.
//
// THE 409: an underfunded wallet returns `DEPOSIT_FUNDS_REQUIRED`. That is a CONSIDERED state, not a
// generic failure — the dialog stays open and routes the admin to where the money is topped up, with
// copy that depends on who may add funds (`mayAddFunds`, EP-SUITE7): someone who may — the base-role
// Owner, or a holder of `company_wallet:deposit` such as a Donations Owner — adds funds themselves;
// anyone else is pointed at an Owner. This mirrors the gift-card wallet-insufficient asymmetry
// (`send-gift-card-dialog.tsx`'s INSUFFICIENT_FUNDS_REASON + the payments tab's
// `paymentMethodsNote`).
//
// A role-read ERROR fails OPEN to the add-funds copy, matching `settings-payment-methods-tab.tsx`: the
// server is the authoritative gate either way, and stranding a real Owner with "ask an Owner" is the
// worse failure. Publishing never promises a winner — the degenerate-competition rule is stated here.

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companiesGetCompanyAdminOptions,
  stepsCompetitionsAdminGetAdminStepsCompetitionQueryKey,
  stepsCompetitionsAdminPublishAdminStepsCompetitionMutation,
} from '@givmo/api-client/query';
import type { AdminStepsCompetitionOut } from '@givmo/api-client';
import { Button, Dialog, testIdProp, toast } from '@givmo/design-system';
import { PORTAL_WORDS, useEmployerModulePaths } from '../../lib/module-paths';
import { mayAddFunds } from '../settings/funding-permissions';
import {
  DEGENERATE_RULE_COPY,
  errorMessage,
  formatPrize,
  isDepositFundsRequired,
  walletAvailable,
  walletCoversPrize,
} from './competitions-decode';

const TEST_ID = 'competitions-publish-dialog';

// The shortfall's words name where funds are added and who can add them, so they are the HOST's
// (`words`, EP-MNT5(h)); these two are the portal's, the default a host that supplies nothing reads.
export const DEPOSIT_OWNER_COPY = PORTAL_WORDS.publishNoFundsSelf;
export const DEPOSIT_MEMBER_COPY = PORTAL_WORDS.publishNoFundsOther;

export interface PublishCompetitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competition: AdminStepsCompetitionOut;
}

type Phase =
  | { status: 'idle' }
  | { status: 'publishing' }
  | { status: 'needs-funds' }
  | { status: 'error'; message: string };

export function PublishCompetitionDialog({
  open,
  onOpenChange,
  competition,
}: PublishCompetitionDialogProps) {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>({ status: 'idle' });

  // Fail-open to the add-funds copy on a role-read error (see the header note).
  const roleQuery = useQuery(companiesGetCompanyAdminOptions());
  const canAddFunds = mayAddFunds({
    settled: roleQuery.isSuccess,
    role: roleQuery.data?.admin_company_role,
    permissions: roleQuery.data?.permissions,
  });

  // Reset only on a genuine CLOSED→OPEN transition, so a reopened dialog never shows a stale outcome.
  useEffect(() => {
    if (open) setPhase({ status: 'idle' });
  }, [open]);

  const publish = useMutation({
    ...stepsCompetitionsAdminPublishAdminStepsCompetitionMutation(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: stepsCompetitionsAdminGetAdminStepsCompetitionQueryKey({
            path: { steps_competition_id: competition.id },
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: ['steps_competitions_admin-list_admin_steps_competitions'],
        }),
      ]);
      setPhase({ status: 'idle' });
      toast.success('Competition published.');
      onOpenChange(false);
    },
    onError: (error) => {
      if (isDepositFundsRequired(error)) {
        setPhase({ status: 'needs-funds' });
        return;
      }
      setPhase({
        status: 'error',
        message: errorMessage(error, 'We couldn’t publish this competition. Please try again.'),
      });
    },
  });

  const busy = publish.isPending;
  const prize = formatPrize(competition);
  const available = walletAvailable(competition);
  // The detail read already carries the wallet's available balance, so a shortfall is knowable BEFORE
  // the click — say so up front rather than making the admin discover it via the 409. The backend stays
  // authoritative (this balance can be stale), so the 409 path below is unchanged; `null` (no money
  // context on the wire) means unknowable, and we claim nothing.
  //
  // Held until the ROLE read settles. The copy forks Owner/Member, and this advisory warning paints on
  // open — rendering it while the role is still pending would show the fail-open Owner instruction and
  // then swap a Member to "ask an Owner" a beat later. The 409 path has no such problem (the admin has
  // clicked by then), and delaying an advisory line costs nothing.
  const shortfallKnownUpFront =
    phase.status === 'idle' && !roleQuery.isPending && walletCoversPrize(competition) === false;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label="Publish competition"
        className="flex flex-col gap-12"
        testId={TEST_ID}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Publish this competition?
        </Dialog.Title>

        <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          Publishing holds the prize from your company balance so it is guaranteed for the winner’s
          charity. The prize is locked once published.
        </Dialog.Description>

        {/* The full cost, stated before the click. The prize IS the cost — no platform fee is added. */}
        <div className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16">
          <div className="flex items-center justify-between">
            <span className="font-sans text-s14 text-text-secondary">Held for this prize</span>
            <span
              className="font-sans text-s15 font-w600 tabular-nums text-text-strong"
              {...testIdProp(`${TEST_ID}-cost`)}
            >
              {prize}
            </span>
          </div>
          {available !== null && (
            <div className="flex items-center justify-between">
              <span className="font-sans text-s14 text-text-secondary">
                Company wallet available
              </span>
              <span
                className="font-sans text-s15 font-w600 tabular-nums text-text-strong"
                {...testIdProp(`${TEST_ID}-available`)}
              >
                {available}
              </span>
            </div>
          )}
          <p className="font-sans text-s12 text-text-tertiary">
            No platform fee is added — the wallet is held for the prize amount.
          </p>
        </div>

        <p className="font-sans text-s12 text-text-tertiary">{DEGENERATE_RULE_COPY}</p>

        {shortfallKnownUpFront && (
          <p
            className="font-sans text-s13 text-warning-700"
            {...testIdProp(`${TEST_ID}-shortfall`)}
          >
            {canAddFunds ? paths.words.publishNoFundsSelf : paths.words.publishNoFundsOther}
          </p>
        )}

        {phase.status === 'needs-funds' && (
          <div
            className="flex flex-col items-start gap-8 rounded-lg border border-warning-100 bg-warning-50 p-16"
            {...testIdProp(`${TEST_ID}-needs-funds`)}
          >
            <p className="font-sans text-s13 text-warning-700">
              {canAddFunds ? paths.words.publishNoFundsSelf : paths.words.publishNoFundsOther}
            </p>
            {canAddFunds && (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  void navigate({
                    to: paths.paymentMethods.to,
                    search: paths.paymentMethods.search,
                  });
                }}
                testId={`${TEST_ID}-add-funds`}
              >
                {paths.paymentMethods.goLabel}
              </Button>
            )}
          </div>
        )}

        {phase.status === 'error' && (
          <p
            className="font-sans text-s13 font-w500 text-danger-700"
            {...testIdProp(`${TEST_ID}-error`)}
          >
            {phase.message}
          </p>
        )}

        <div className="mt-12 flex items-center justify-end gap-8">
          <Dialog.Close
            render={<Button appearance="saas" tone="secondary" size="md" disabled={busy} />}
            {...testIdProp(`${TEST_ID}-cancel`)}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            loading={busy}
            onClick={() => {
              setPhase({ status: 'publishing' });
              publish.mutate({ path: { steps_competition_id: competition.id } });
            }}
            testId={`${TEST_ID}-confirm`}
          >
            {phase.status === 'needs-funds' || phase.status === 'error'
              ? 'Try again'
              : `Publish and hold ${prize}`}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
